<script setup>
import { computed, ref, watch } from "vue";

/**
 * Square avatar cropper (GH-1217), no dependencies.
 *
 * The photo sits inside a fixed square frame at a scale that covers it;
 * dragging pans, the slider (or +/−) zooms from 1× to 3×, and the arrow
 * keys nudge. A round mask shows what the circular avatar will keep. The
 * model is the crop rectangle as percentages of the original per axis —
 * exactly what Cloudinary's relative c_crop wants — so a pixel-square crop
 * of a non-square photo has different w and h percentages.
 *
 * Nothing is emitted until the member moves something: a fresh photo keeps
 * Cloudinary's automatic framing (null) until they choose otherwise.
 */
const props = defineProps({
    src: {
        type: String,
        required: true,
    },
    // { x, y, w, h } percentages of the original, or null for automatic.
    modelValue: {
        type: Object,
        default: null,
    },
    // Frame size in CSS pixels.
    size: {
        type: Number,
        default: 240,
    },
    alt: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:modelValue"]);

const MAX_ZOOM = 3;

const natural = ref(null); // { width, height } of the loaded image
const zoom = ref(1);
const offset = ref({ x: 0, y: 0 }); // image top-left relative to the frame
const dragging = ref(false);
let dragStart = null;

// Scale at 1× so the shorter side exactly fills the frame.
const baseScale = computed(() =>
    natural.value
        ? props.size / Math.min(natural.value.width, natural.value.height)
        : 1,
);
const scale = computed(() => baseScale.value * zoom.value);
// Rounded to 1/100px so float noise never leaks into the inline styles.
const px = (value) => Math.round(value * 100) / 100;
const displayWidth = computed(() =>
    natural.value ? px(natural.value.width * scale.value) : props.size,
);
const displayHeight = computed(() =>
    natural.value ? px(natural.value.height * scale.value) : props.size,
);

function clampOffset({ x, y }) {
    return {
        x: px(Math.min(0, Math.max(props.size - displayWidth.value, x))),
        y: px(Math.min(0, Math.max(props.size - displayHeight.value, y))),
    };
}

// Keep the same image point under the frame's centre while zooming.
function setZoom(next) {
    const previous = scale.value;
    zoom.value = Math.min(MAX_ZOOM, Math.max(1, next));
    const ratio = scale.value / previous;
    const centre = props.size / 2;
    offset.value = clampOffset({
        x: centre - (centre - offset.value.x) * ratio,
        y: centre - (centre - offset.value.y) * ratio,
    });
    emitCrop();
}

function emitCrop() {
    if (!natural.value) {
        return;
    }

    // `|| 0` folds a negative zero from the clamp back to plain 0.
    const round = (value) => Math.round(value * 100) / 100 || 0;
    const cropSize = props.size / scale.value;

    emit("update:modelValue", {
        x: round((-offset.value.x / scale.value / natural.value.width) * 100),
        y: round((-offset.value.y / scale.value / natural.value.height) * 100),
        w: round((cropSize / natural.value.width) * 100),
        h: round((cropSize / natural.value.height) * 100),
    });
}

// Seed zoom/offset from a saved crop; centre at 1× otherwise.
function applyModel() {
    if (!natural.value) {
        return;
    }

    const crop = props.modelValue;

    if (crop && crop.w > 0) {
        const cropWidth = (crop.w / 100) * natural.value.width;
        zoom.value = Math.min(
            MAX_ZOOM,
            Math.max(1, props.size / (cropWidth * baseScale.value)),
        );
        offset.value = clampOffset({
            x: -(crop.x / 100) * natural.value.width * scale.value,
            y: -(crop.y / 100) * natural.value.height * scale.value,
        });
    } else {
        zoom.value = 1;
        offset.value = clampOffset({
            x: (props.size - displayWidth.value) / 2,
            y: (props.size - displayHeight.value) / 2,
        });
    }
}

function onLoad(event) {
    natural.value = {
        width: event.target.naturalWidth,
        height: event.target.naturalHeight,
    };
    applyModel();
}

watch(
    () => props.modelValue,
    (value) => {
        if (value === null) {
            applyModel();
        }
    },
);

function onPointerDown(event) {
    dragging.value = true;
    dragStart = {
        x: event.clientX - offset.value.x,
        y: event.clientY - offset.value.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
}

function onPointerMove(event) {
    if (!dragging.value) {
        return;
    }

    offset.value = clampOffset({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
    });
    emitCrop();
}

function onPointerUp() {
    dragging.value = false;
}

const nudges = {
    ArrowLeft: { x: 1, y: 0 },
    ArrowRight: { x: -1, y: 0 },
    ArrowUp: { x: 0, y: 1 },
    ArrowDown: { x: 0, y: -1 },
};

function onKeydown(event) {
    if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        setZoom(zoom.value + 0.1);
        return;
    }

    if (event.key === "-") {
        event.preventDefault();
        setZoom(zoom.value - 0.1);
        return;
    }

    const nudge = nudges[event.key];

    if (!nudge) {
        return;
    }

    event.preventDefault();
    const step = props.size * (event.shiftKey ? 0.1 : 0.02);
    offset.value = clampOffset({
        x: offset.value.x + nudge.x * step,
        y: offset.value.y + nudge.y * step,
    });
    emitCrop();
}
</script>

<template>
    <div class="flex flex-col items-center gap-3">
        <div
            class="relative shrink-0 cursor-grab touch-none select-none overflow-hidden rounded-[5px] bg-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
            :class="dragging ? 'cursor-grabbing' : ''"
            :style="{ width: `${size}px`, height: `${size}px` }"
            tabindex="0"
            role="application"
            aria-label="Crop profile photo. Drag to move, plus and minus to zoom, arrow keys to nudge."
            data-test="avatar-crop-frame"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @keydown="onKeydown"
        >
            <img
                :src="src"
                :alt="alt"
                draggable="false"
                class="absolute left-0 top-0 max-w-none"
                :style="{
                    width: `${displayWidth}px`,
                    height: `${displayHeight}px`,
                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                }"
                data-test="avatar-crop-image"
                @load="onLoad"
            />
            <!-- Round mask: what the circular avatar keeps -->
            <div
                class="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_9999px_rgba(16,42,61,0.55)]"
                aria-hidden="true"
            ></div>
        </div>

        <label
            class="flex w-full max-w-[240px] items-center gap-3 text-[13px] text-[#3c3938]"
        >
            <span class="shrink-0">Zoom</span>
            <input
                type="range"
                min="1"
                :max="MAX_ZOOM"
                step="0.01"
                :value="zoom"
                class="w-full accent-brand-red"
                aria-label="Zoom"
                data-test="avatar-crop-zoom"
                @input="setZoom(Number($event.target.value))"
            />
        </label>
    </div>
</template>
